import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, RefreshCw, Save, Check, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * AIHealthSummary — comprehensive GPT-5.2 generated Breast Intelligence
 * analysis (Bulgarian). 4-6 short paragraphs synthesising all 8 BHI
 * sections. Cached server-side for 24h.
 *
 * Adds a "Запази в Health Intelligence" button that saves the snapshot
 * (full JSON + AI text) and keeps the last 10 per user.
 */
export default function AIHealthSummary({ isDark = false }) {
  const { API, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const fetchSummary = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const url = `${API}/breast-intelligence/ai-summary${force ? "?force=1" : ""}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (e) {
      setData({ summary: "Обобщението не е достъпно в момента.", error: true });
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchSnapshots = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/snapshots`);
      setSnapshots(data?.snapshots || []);
    } catch (_e) { /* ignore */ }
  }, [API]);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const { data: out } = await axios.post(`${API}/breast-intelligence/snapshots/save`, {});
      setSavedId(out.id);
      toast.success("Запазено в Health Intelligence");
      fetchSnapshots();
      setTimeout(() => setSavedId(null), 4000);
    } catch (_e) {
      toast.error("Не успяхме да запазим snapshot-а.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSummary();
      fetchSnapshots();
    }
  }, [user, fetchSummary, fetchSnapshots]);

  if (!data && !loading) return null;

  // Split AI text into paragraphs for nicer rendering
  const paragraphs = (data?.summary || "").split(/\n\n+/).filter(Boolean);
  const showCollapse = paragraphs.length > 2;
  const visibleParas = expanded || !showCollapse ? paragraphs : paragraphs.slice(0, 2);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-6 p-5 rounded-2xl relative overflow-hidden ${
        isDark
          ? "bg-gradient-to-br from-pink-500/15 via-purple-500/10 to-rose-500/15 border border-pink-500/30"
          : "bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50 border border-pink-200"
      }`}
      data-testid="ai-health-summary"
    >
      <div className="absolute top-0 right-0 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="flex items-start gap-3 relative">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          isDark ? "bg-pink-500/30" : "bg-pink-200"
        }`}>
          <Sparkles className={`w-5 h-5 ${isDark ? "text-pink-200" : "text-pink-700"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <span className="text-xs font-bold opacity-70 uppercase tracking-wider">
              AI обобщение за Вашата гръдна интелигентност
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fetchSummary(true)}
                disabled={loading}
                className="text-[10px] opacity-50 hover:opacity-100 flex items-center gap-1 px-2 py-1 rounded"
                data-testid="ai-summary-refresh"
                title="Регенерирай (пропуска 24-часовия кеш)"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {loading && !data ? (
            <div className="space-y-2">
              <div className="h-3 rounded bg-white/30 animate-pulse w-3/4" />
              <div className="h-3 rounded bg-white/30 animate-pulse w-5/6" />
              <div className="h-3 rounded bg-white/30 animate-pulse w-2/3" />
              <div className="h-3 rounded bg-white/30 animate-pulse w-4/5" />
            </div>
          ) : (
            <div className="space-y-3" data-testid="ai-summary-text">
              {visibleParas.map((p, i) => (
                <p key={i} className="text-sm sm:text-[15px] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          )}

          {showCollapse && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-semibold text-pink-600 dark:text-pink-300 hover:opacity-80 inline-flex items-center gap-1 mt-2"
              data-testid="ai-summary-expand"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" /> Покажи по-малко
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" /> Прочети целия анализ ({paragraphs.length - 2} още {paragraphs.length - 2 === 1 ? "абзац" : "абзаца"})
                </>
              )}
            </button>
          )}

          {/* Action row */}
          {!loading && data?.summary && !data?.error && (
            <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-pink-200/30 dark:border-pink-500/20 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={saveSnapshot}
                  disabled={saving || !!savedId}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all ${
                    savedId
                      ? "bg-emerald-500 text-white"
                      : "bg-pink-600 hover:bg-pink-700 text-white"
                  } disabled:opacity-60`}
                  data-testid="ai-summary-save-snapshot"
                >
                  {savedId ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Запазено
                    </>
                  ) : (
                    <>
                      <Save className={`w-3.5 h-3.5 ${saving ? "animate-pulse" : ""}`} />
                      Запази в Health Intelligence
                    </>
                  )}
                </button>
                {snapshots.length > 0 && (
                  <Link
                    to="/dashboard/health-intelligence?tab=snapshots"
                    className="text-xs opacity-60 hover:opacity-100 flex items-center gap-1"
                    data-testid="ai-summary-view-snapshots"
                  >
                    <BookOpen className="w-3.5 h-3.5" /> {snapshots.length} запазени
                  </Link>
                )}
              </div>
              {data?.cached && (
                <span className="text-[10px] opacity-40">
                  Кеширано · {data.age_hours}ч
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
